import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootState, IRootThunkDispatch } from "../../redux/store";
import { Button, Grid } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
    EditNote,
    Article,
    CheckCircleOutline,
    ErrorOutline,
} from "@mui/icons-material";
import moment, { Moment } from "moment";
import { AttendanceCollectionInfo } from "../../redux/collection/state";
import { OpenLoadingBarThunk } from "../../redux/common/isLoading/thunk";
import {
    axiosAPIWithJson,
    axiosGet,
    axiosPostWithFormData,
    axiosPutWithJson,
} from "../../util/axios";
import {
    OpenSnackBarWithResultFail,
    OpenSnackBarWithResultSuccess,
} from "../../redux/common/snackBar/thunk";
import {
    initCollectionAttendanceData,
    initCollectionDetailThunk,
    initSelectedEditattendanceThunk,
} from "../../redux/collection/thunk";
import FormDialog from "../common/FormDialog";
import CollectionDetail from "./CollectionDetail";
import styles from "./collectionAttendance.module.css";
import { isGovernemnt, isSuperAdmin } from "../../util/form/form";
import EditAttendance from "../common/EditAttendance";

function CollectionAttendace() {
    const { user } = useSelector((state: IRootState) => state.userAuth);
    const isGovernmentRole = isGovernemnt(user?.token?.role_id as number);
    const isSuperAdminRole = isSuperAdmin(user?.token?.role_id as number);
    const [detailDialog, setDetailDialog] = useState<boolean>(false);
    const [editDialog, setEditDialog] = useState<boolean>(false);
    const [resendDialog, setResendDialog] = useState<boolean>(false);
    const [collectionDate, setCollectionDate] = useState<Date | Moment | null>(
        new Date()
    );
    const [collectionId, setCollectionId] = useState<number>(0);
    const [remark, setReamark] = useState<string>("");
    const dispatch = useDispatch<IRootThunkDispatch>();
    const {
        attendanceCollectionInfo,
        selectedEditattendanceCollection,
        binInfo,
    } = useSelector((state: IRootState) => state.collectionState);

    async function handleClickCollectionDetailButton(
        attendanceCollectionInfo: AttendanceCollectionInfo
    ) {
        dispatch(OpenLoadingBarThunk(true));

        const result = await axiosGet(
            `/api/collection/detail/${attendanceCollectionInfo.collection_id}`
        );
        setCollectionId(attendanceCollectionInfo.collection_id);
        setReamark(attendanceCollectionInfo.remark);
        setCollectionDate(attendanceCollectionInfo.collection_date);
        dispatch(OpenLoadingBarThunk(false));

        if (result.data.success) {
            dispatch(
                initCollectionDetailThunk(
                    result.data.collectionDetailList,
                    attendanceCollectionInfo.signature_image,
                    attendanceCollectionInfo.signature_image_thumb,
                    result.data.collectionPhotoList
                )
            );
            setDetailDialog(true);
        } else {
            dispatch(OpenSnackBarWithResultFail("未能取得詳情收集資料"));
        }
    }

    function handleCloseDialog() {
        setDetailDialog(false);
        setEditDialog(false);
        setResendDialog(false);
    }

    function handleClickEditButton(
        attendanceCollectionInfo: AttendanceCollectionInfo
    ) {
        dispatch(initSelectedEditattendanceThunk(attendanceCollectionInfo));
        setEditDialog(true);
    }

    async function handleSubmitEdit() {
        const data = {
            binInfo: binInfo,
            selectedEditattendanceCollection: selectedEditattendanceCollection,
        };

        const result = await axiosPutWithJson(
            `/api/collection/attendance/${
                (
                    data.selectedEditattendanceCollection as AttendanceCollectionInfo
                ).collection_id
            }`,
            data
        );
        if (result.data.success) {
            dispatch(OpenSnackBarWithResultSuccess(result.data.message));

            dispatch(OpenLoadingBarThunk(true));
            const attendance_table = await axiosGet(
                `/api/collection/attendance/${
                    (
                        selectedEditattendanceCollection as AttendanceCollectionInfo
                    ).route_id
                }`
            );
            dispatch(OpenLoadingBarThunk(false));
            if (attendance_table.data.success) {
                dispatch(
                    initCollectionAttendanceData(
                        attendance_table.data.routeList
                    )
                );
            } else {
                dispatch(OpenSnackBarWithResultFail("未能取得詳情資料"));
            }
        } else {
            dispatch(OpenSnackBarWithResultFail(result.data.message));
        }
        setEditDialog(false);
    }

    function handleClickResendButton(
        attendanceCollectionInfo: AttendanceCollectionInfo
    ) {
        dispatch(initSelectedEditattendanceThunk(attendanceCollectionInfo));
        setResendDialog(true);
    }

    async function handleResendEmail() {
        const info =
            selectedEditattendanceCollection as AttendanceCollectionInfo;

        dispatch(OpenLoadingBarThunk(true));
        const result = await axiosAPIWithJson.post(
            "/api/collection/email/resend",
            { collection_id: info.collection_id.toString() }
        );

        if (result.data.success) {
            dispatch(OpenSnackBarWithResultSuccess(result.data.message));

            const attendance_table = await axiosGet(
                `/api/collection/attendance/${
                    (
                        selectedEditattendanceCollection as AttendanceCollectionInfo
                    ).route_id
                }`
            );
            if (attendance_table.data.success) {
                dispatch(
                    initCollectionAttendanceData(
                        attendance_table.data.routeList
                    )
                );
            } else {
                dispatch(OpenSnackBarWithResultFail("未能取得詳情資料"));
            }
        } else {
            dispatch(OpenSnackBarWithResultFail(result.data.message));
        }
        dispatch(OpenLoadingBarThunk(false));
        setResendDialog(false);
    }

    const columns: GridColDef[] = [
        {
            field: "bin_id",
            headerName: "桶編號",
            flex: 1,
            renderCell: (data) => (
                <div
                    className={
                        data.row.is_additional === 1 ? styles.isAdditonal : ""
                    }
                >
                    {data.value}
                </div>
            ),
        },
        {
            field: "code",
            headerName: "回收桶號碼",
            flex: 1,
        },
        { field: "location_cn", headerName: "回收桶地址", flex: 3 },
        {
            field: "time_start",
            headerName: "預定收集時間/預定時間",
            flex: 2,
            renderCell: (data) => (
                <div>
                    {data.value}-{data.row.time_end}
                </div>
            ),
        },
        {
            field: "attendance_date",
            headerName: "打卡時間",
            flex: 2,
            renderCell: (data) => (
                <div>
                    {data.value
                        ? moment.utc(data.value).format("YYYY-MM-DD HH:mm:ss")
                        : null}
                </div>
            ),
        },
        {
            field: "attendance_place",
            headerName: "打卡地點",
            flex: 4,
        },

        {
            field: "collection_date",
            headerName: "到達時間",
            flex: 2,
            renderCell: (data) => (
                <div>
                    {data.value
                        ? moment.utc(data.value).format("YYYY-MM-DD HH:mm:ss")
                        : null}
                </div>
            ),
        },
        {
            field: "contract",
            headerName: "位置",
            flex: 1,
        },
        {
            field: "action",
            headerName: "收集詳情",
            flex: 2,
            renderCell: (data) => (
                <div>
                    {data.row.collection_image_id ||
                    (data.row.contract_id === 2 && data.row.attendance_date) ? (
                        <Button
                            variant="contained"
                            onClick={() =>
                                handleClickCollectionDetailButton(data.row)
                            }
                        >
                            <Article />
                            <Grid>詳情</Grid>
                        </Button>
                    ) : (
                        <></>
                    )}
                </div>
            ),
        },
        {
            field: "edit",
            headerName: "修改紀錄",
            flex: 2,
            renderCell: (data) => (
                <div>
                    <Button
                        variant="contained"
                        onClick={() => handleClickEditButton(data.row)}
                        className={styles.editBtn}
                    >
                        <EditNote />
                        <Grid>更改</Grid>
                    </Button>
                </div>
            ),
        },
        {
            field: "email_record",
            headerName: "電郵紀錄",
            flex: 2,
            renderCell: (data) => (
                <div>
                    {data.row.contract_id === 2 && data.row.attendance_date ? (
                        <Button
                            variant="contained"
                            onClick={() => handleClickResendButton(data.row)}
                            className={
                                data.row.email_sent
                                    ? styles.success
                                    : styles.fail
                            }
                        >
                            {data.row.email_sent ? (
                                <CheckCircleOutline />
                            ) : (
                                <ErrorOutline />
                            )}

                            <Grid>重發電郵</Grid>
                        </Button>
                    ) : (
                        <></>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div>
            <Grid>本車線共 {attendanceCollectionInfo.length} 個收集點</Grid>

            <div className={styles.demoOuterContainer}>
                <div className={styles.demoContainer}></div>
                <div>為司機新增路線</div>
            </div>

            <div style={{ height: "100%", width: "100%" }}>
                <DataGrid
                    rows={attendanceCollectionInfo || []}
                    columns={columns}
                    initialState={{
                        pagination: {
                            paginationModel: { page: 0, pageSize: 30 },
                        },
                    }}
                    pageSizeOptions={[5, 10]}
                    checkboxSelection
                    getRowId={(row) => row.code}
        
                />
            </div>

            <FormDialog
                title="收集詳細情況"
                open={detailDialog}
                handleClose={handleCloseDialog}
                size="lg"
            >
                <CollectionDetail
                    remark={remark}
                    collectionId={collectionId}
                    collectionDate={collectionDate}
                />
            </FormDialog>

            <FormDialog
                title="更改收集情況"
                open={editDialog}
                handleClose={handleCloseDialog}
                handleSubmit={handleSubmitEdit}
                size="lg"
            >
                <EditAttendance mode="Collection" />
            </FormDialog>

            <FormDialog
                title="重發電郵"
                open={resendDialog}
                handleClose={handleCloseDialog}
                handleSubmit={handleResendEmail}
                size="xs"
            >
                <>確定要重發電郵?</>
            </FormDialog>
        </div>
    );
}

export default CollectionAttendace;
